/**
 * Utility functions for AdSense integration and tracking prevention detection
 */

// Check if tracking prevention is blocking ads
export const isTrackingPreventionEnabled = (): boolean => {
  try {
    // Test if localStorage is accessible (common tracking prevention target)
    const testKey = '__ads_tracking_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    
    // Firefox Enhanced Tracking Protection is more aggressive
    if (navigator.userAgent.includes('Firefox')) {
      // Firefox often blocks third-party cookies and tracking by default
      return true;
    }
    
    return false;
  } catch (error) {
    return true;
  }
};

// Detect Firefox Enhanced Tracking Protection
export const isFirefoxETPEnabled = (): boolean => {
  return navigator.userAgent.includes('Firefox');
};

// Check if AdSense is likely blocked
export const isAdSenseBlocked = (): boolean => {
  try {
    // Check if adsbygoogle is available
    return !window.adsbygoogle;
  } catch (error) {
    return true;
  }
};

// Detect if ad blocker is blocking AdSense scripts
export const isAdBlockerActive = (): Promise<boolean> => {
  return new Promise((resolve) => {
    // Create a test ad element
    const testAd = document.createElement('div');
    testAd.innerHTML = '&nbsp;';
    testAd.className = 'adsbox';
    testAd.style.cssText = 'position:absolute;left:-10000px;top:-1000px;width:1px;height:1px;';
    
    document.body.appendChild(testAd);
    
    // Check if the ad element was blocked
    setTimeout(() => {
      const isBlocked = testAd.offsetHeight === 0;
      document.body.removeChild(testAd);
      resolve(isBlocked);
    }, 100);
  });
};

// Check if AdSense script failed to load due to ad blocker
export const checkAdSenseScriptLoad = (): Promise<boolean> => {
  return new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = 'https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-1421631996968165';
    script.async = true;
    script.crossOrigin = 'anonymous';
    
    script.onload = () => {
      resolve(true); // Script loaded successfully
    };
    
    script.onerror = () => {
      resolve(false); // Script failed to load (likely blocked)
    };
    
    // Remove the script after testing
    setTimeout(() => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    }, 5000);
    
    document.head.appendChild(script);
  });
};

// Get user-friendly error message for ad issues
export const getAdErrorMessage = (): string => {
  if (isTrackingPreventionEnabled()) {
    return 'Ad blocking detected. Please disable tracking prevention or ad blockers to see ads.';
  }
  if (isAdSenseBlocked()) {
    return 'Ad service temporarily unavailable.';
  }
  return 'Ad loading failed.';
};

// Safe localStorage operations for ad preferences
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.log('localStorage access blocked:', error);
      return null;
    }
  },
  
  setItem: (key: string, value: string): boolean => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      console.log('localStorage access blocked:', error);
      return false;
    }
  },
  
  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      console.log('localStorage access blocked:', error);
      return false;
    }
  }
};

// Check if ads should be shown based on user preferences and tracking prevention
export const shouldShowAds = (): boolean => {
  const userDisabledAds = safeLocalStorage.getItem('adsClosed') === 'true' || 
                          safeLocalStorage.getItem('mobileAdsClosed') === 'true';
  
  if (userDisabledAds) {
    return false;
  }
  
  // Always try to show ads, let AdSense handle the blocking gracefully
  // The tracking prevention warnings are normal and don't prevent ad display
  return true;
};

// Suppress tracking prevention console warnings for better user experience
export const suppressTrackingWarnings = (): void => {
  // Override console methods to filter out tracking prevention messages
  const originalWarn = console.warn;
  const originalError = console.error;
  const originalLog = console.log;

  const shouldSuppress = (message: string): boolean => {
    return message.includes('Tracking Prevention blocked') || 
           message.includes('storage for') ||
           message.includes('postMessage') ||
           message.includes('frame-src') ||
           message.includes('sodar2.js') ||
           message.includes('show_ads_impl_fy2021.js') ||
           message.includes('zrt_lookup_fy2021.html') ||
           message.includes('gen_204?id=ach_evt') ||
           message.includes('pagead/ads?client=ca-pub-') ||
           message.includes('pagead/id') ||
           message.includes('ad_status.js') ||
           message.includes('sodar?id=sodar2') ||
           message.includes('bg/JC95ub0RqwIqGkOag_UBKJ1rVEFfMsHldUmzjWKWEz4.js') ||
           // Firefox-specific warnings
           message.includes('OpaqueResponseBlocking') ||
           message.includes('Feature Policy: Skipping unsupported feature') ||
           message.includes('Content-Security-Policy: Couldn\'t process unknown directive') ||
           message.includes('Cookie warnings') ||
           message.includes('Partitioned cookie or storage access') ||
           message.includes('unreachable code after return statement') ||
           message.includes('Source map error') ||
           message.includes('Web Console logging API has been disabled') ||
           message.includes('Quirks Mode') ||
           message.includes('SameSite') ||
           message.includes('cross-site context');
  };

  console.warn = (...args: any[]) => {
    const message = args.join(' ');
    if (shouldSuppress(message)) {
      return;
    }
    originalWarn.apply(console, args);
  };

  console.error = (...args: any[]) => {
    const message = args.join(' ');
    if (shouldSuppress(message)) {
      return;
    }
    originalError.apply(console, args);
  };

  console.log = (...args: any[]) => {
    const message = args.join(' ');
    if (shouldSuppress(message)) {
      return;
    }
    originalLog.apply(console, args);
  };

  // Also override the global console methods if they exist
  if (typeof window !== 'undefined') {
    const originalConsole = window.console;
    if (originalConsole) {
      window.console = {
        ...originalConsole,
        warn: console.warn,
        error: console.error,
        log: console.log
      };
    }
  }
};
